/** @format */

import database from '../firebase/config';
import { observable, action } from 'mobx';
import RequestsStore from './RequestsStore';
import ClientsStore from './ClientsStore';
import sortSales from '../helpers/sortSales';
import { newSale, newBox, newShipping } from '../models/requests';
import { validateForm, formModels } from '../helpers/validateForm';
import { getTimestamp } from '../helpers/getTimestamp';
import getTimestampFromDate from '../helpers/getTimestampFromDate';

class SalesStore {
  @observable sales = {};

  @observable salesParams = {
    advancedSearch: {
      active: false,
      priceRange: {
        min: 0,
        max: 2000,
      },
      dateRange: {
        start: getTimestamp(),
        end: getTimestamp() + 604800,
      },
    },
    editSaleForm: {
      open: false,
      requestId: null,
    },
    searchValue: '',
    searchNotFound: false,
    itemLoaded: 20,
    filter: {
      active: false,
      progress: null,
    },
    orderBy: {
      prop: 'timestamp',
      direction: 'asc',
    },
  };

  @observable currentSale = {
    data: {
      _sellerId: null,
      _clientId: null,
      _requestId: null,
      items: {},
      status: 'new',
      shipping: {
        boxes: {},
        _addressId: 0,
        address: null,
        shippingCost: 0,
      },
      notes: null,
      tot: 0,
    },
  };

  @observable currentBox = {
    trackingCode: null,
    trackingLink: null,
    method: null,
    width: 0,
    height: 0,
    depth: 0,
    volume: 0,
    weight: 0,
    taxableVolume: 0,
    price: 0,
  };

  @observable salesErrors = {
    boxes: [],
  };

  @action fetchSales = () => {
    const advancedSearch = this.salesParams.advancedSearch.active;
    if (!advancedSearch) {
      let salesRef = database
        .ref('/sales/')
        .orderByChild('/data/timestamp')
        .limitToFirst(this.salesParams.itemLoaded);
      salesRef.off('value');
      salesRef.on('value', snapshot => {
        this.sales = snapshot.val();
      });
    } else {
      let salesRef = database
        .ref('/sales/')
        .orderByChild('/data/timestamp')
        .startAt(this.salesParams.advancedSearch.dateRange.start)
        .endAt(this.salesParams.advancedSearch.dateRange.end);
      salesRef.off('value');
      salesRef.on('value', snapshot => {
        this.sales = snapshot.val();
      });
    }
  };

  @action loadMoreSales = () => {
    this.salesParams.itemLoaded += 20;
    this.fetchSales();
  };

  @action searchSale = value => {
    this.salesParams.searchValue = value;
  };

  @action openAdvancedSearch = () => {
    if (this.salesParams.advancedSearch.active === true) {
      this.salesParams.advancedSearch.active = false;
    } else {
      this.salesParams.advancedSearch.active = true;
    }
    this.fetchSales();
  };

  @action timeIntervalInput = (date, name) => {
    this.salesParams.advancedSearch.dateRange[name] = -getTimestampFromDate(
      date,
    );
    this.fetchSales();
  };

  @action priceRangeInput = (value, name) => {
    this.salesParams.advancedSearch.priceRange[name] = value;
  };

  @action sortSalesBy = key => {
    let prop = null;
    let salesParams = this.salesParams;
    this.salesParams = sortSales(key, salesParams, prop);
  };

  @action openSaleForm = requestId => {
    this.salesParams.editSaleForm.open = true;
    this.resetCurrentSale();
    this.loadCurrentSale();
  };

  @action resetCurrentSale = () => {
    this.currentSale.data.shipping = { ...newShipping };
    this.currentSale.data = { ...newSale.data };
    this.currentBox = { ...newBox };
    this.currentSale.data.shipping.boxes = {};
    this.salesParams.editSaleForm.requestId = null;
  };

  @action closeSaleForm = reFetchRequests => {
    this.salesParams.editSaleForm.open = false;
  };

  @action resetBoxFormErrors = () => {
    this.salesErrors.boxes = [];
  };

  @action handleNewBoxInput = (name, value, options) => {
    this.currentBox[name] = value;
    if (name === 'method') {
      this.currentBox['_' + name] = value;
    }
    if (options !== undefined && options !== null) {
      options.forEach(option => {
        if (option.value === value) {
          this.currentBox[name] = option.text;
        }
      });
    }
  };

  @action handleNewBoxSubmit = () => {
    const $this = this;
    let boxesKeys = Object.keys($this.currentSale.data.shipping.boxes);
    let newBoxKey = boxesKeys.length;
    let errors = validateForm($this.currentBox, formModels.boxes);

    if (errors.length > 0) {
      $this.salesErrors.boxes = errors;
    } else {
      $this.currentSale.data.shipping.boxes[newBoxKey] = {
        ...$this.currentBox,
      };
      let boxesKeys = Object.keys($this.currentSale.data.shipping.boxes);
      $this.currentSale.data.tot =
        Number($this.currentSale.data.tot) -
        Number($this.currentSale.data.shipping.shippingCost);
      $this.currentSale.data.shipping.shippingCost = 0;
      boxesKeys.forEach(key => {
        $this.currentSale.data.shipping.shippingCost =
          Number($this.currentSale.data.shipping.shippingCost) +
          Number($this.currentSale.data.shipping.boxes[key].price);
      });
      $this.currentSale.data.tot =
        Number($this.currentSale.data.tot) +
        Number($this.currentSale.data.shipping.shippingCost);
      $this.currentBox = { ...newBox };
    }
  };

  @action handleShippingTypeSelect = value => {
    this.currentSale.data.shippingType = value;
  };

  @action loadCurrentSale = saleId => {
    const $this = this;
    if (saleId === null || saleId === undefined) {
      let soldOptions = RequestsStore.currentRequest.soldOptions;
      $this.currentSale.data._sellerId =
        RequestsStore.currentRequest.data._seller;
      $this.currentSale.data._requestId =
        RequestsStore.requestsParams.currentRequestId;
      $this.currentSale.data._clientId =
        ClientsStore.clientsParams.currentClientId;
      $this.currentSale.data.clientName = ClientsStore.clientFullName;
      $this.currentSale.data.shipping_addressId = 0;
      $this.currentSale.data.tot = 0;
      $this.currentSale.data.shippingType =
        RequestsStore.currentRequest.data.reqShippingType;
      $this.currentSale.data.items = [];
      soldOptions.forEach((soldOption, index) => {
        let currentPart =
          RequestsStore.currentRequest.reqParts[soldOption._partId];
        let currentOption =
          RequestsStore.currentRequest.reqParts[soldOption._partId].options[
            soldOption._optionKey
          ];
        let partName = currentPart.data.part;
        let carSide = currentPart.data.carSide;
        let carMaker = currentPart.data.carMaker;
        let carModel = currentPart.data.carModel;
        let carVersion = currentPart.data.carVersion;
        let internalCode = currentOption.data.internalCode;
        let position = currentOption.data.position;
        let price = currentOption.data.price;

        this.currentSale.data.items[index] = {
          data: {
            _partId: soldOption._partId,
            _optionKey: soldOption._optionKey,
            name:
              partName +
              ' ' +
              carSide +
              ' ' +
              carMaker +
              ' ' +
              carModel +
              ' ' +
              carVersion,
            internalCode: internalCode,
            position: position,
            sync: false,
            price: price,
          },
        };

        this.currentSale.data.tot =
          Number(this.currentSale.data.tot) + Number(price);
      });
    } else {
      let $this = this;
      let saleRef = database.ref('/sales/' + saleId);
      saleRef.once('value', snapshot => {
        $this.currentSale = snapshot.val();
        let clientId = $this.currentSale.data._clientId;
        RequestsStore.loadCurrentRequest(saleId, null, null, true);
        ClientsStore.loadCurrentClient(clientId);
      });
    }
  };

  @action assignKey = () => {
    let salesKeys = Object.keys(this.sales);
    let salesValues = Object.values(this.sales);
    salesValues.forEach((sale, index) => {
      sale.data.key = salesKeys[index];
    });
  };

  @action handleSelectSaleAddress = input => {
    let $this = this;
    let addresses = input.options;
    addresses.forEach(address => {
      if (address.value === input.value) {
        $this.currentSale.data.shipping.address = address.text;
        $this.currentSale.data.shipping.value = address.value;
      }
    });
  };

  @action handleSaleFormSubmit = () => {
    let $this = this;

    let currentRequestId = RequestsStore.requestsParams.currentRequestId;
    let currentClientId = ClientsStore.clientsParams.currentClientId;
    let currentClientSalesNum = Number(
      ClientsStore.currentClient.data.salesNum,
    );
    let currentClientSalesValue = Number(
      ClientsStore.currentClient.data.salesValue,
    );
    let currentSalesValue = Number($this.currentSale.data.tot);
    let salesRef = database.ref('/sales/' + currentRequestId);
    let requestsRef = database.ref('/requests/' + currentRequestId);
    let clientSalesValRef = database.ref(
      '/clients/' + currentClientId + '/data/salesValue/',
    );
    let clientSalesNumRef = database.ref(
      '/clients/' + currentClientId + '/data/salesNum/',
    );
    RequestsStore.currentRequest.data.status = 'archiviata';
    RequestsStore.currentRequest.data.progress = 'completata con vendita';
    this.currentSale.data.timestamp = getTimestamp();
    this.currentBox = { ...newBox };
    salesRef.set($this.currentSale).then(() => {
      requestsRef.set(RequestsStore.currentRequest).then(() => {
        clientSalesValRef.set(currentClientSalesValue + currentSalesValue);
        clientSalesNumRef.set(currentClientSalesNum + 1);
      });
    });
  };

  @action saleManaged = () => {
    this.currentSale.data.status = 'gestita';
    let currentSaleId = this.currentSale.data._requestId;
    let salesRef = database.ref('/sales/' + currentSaleId);
    salesRef.set(this.currentSale);
  };
}

export default new SalesStore();
